import {ActivityAction, ActivityStatus, IActivity} from "../../activities/activity";
import {createFeatureSelector, createSelector} from "@ngrx/store";
import {DbStoreState} from "../reducers";
import {IOngoingActivitiesState, IWebsiteOngoingActivitiesState} from "../reducers/ongoing-activities.reducer";
import {TypeUtils} from "../../shared/utils/type-utils";
import {Config} from '../../config/config';

const getDbStoreState = createFeatureSelector<DbStoreState>('dbStore');

export const getOngoingActivitiesState = createSelector(getDbStoreState, (dbStoreState: DbStoreState) => dbStoreState.ongoingActivities);

export const getOngoingActivities = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity[] => {
    const activities: IActivity[] = Object.values(state.entities)
      .filter((websiteActivitiesState: IWebsiteOngoingActivitiesState): boolean => !!websiteActivitiesState.entities)
      .map((websiteActivitiesState: IWebsiteOngoingActivitiesState): IActivity[] => Object.values(websiteActivitiesState.entities))
      .reduce((activities: IActivity[], websiteActivities: IActivity[]): IActivity[] => {
        return activities.concat(websiteActivities);
      }, [])
      .filter((activity: IActivity) => {
        const hasAction = TypeUtils.isDefined(activity.action);
        const isNotIgnored = !Config.ignoredOngoingActivities.includes(activity.action);
        const isMigrateWebsite = activity.action === ActivityAction.MIGRATE_WEBSITE;
        const isRelevantNonMigrate = activity.action !== ActivityAction.MIGRATE_WEBSITE && activity.websiteId !== 0;

        return hasAction && isNotIgnored && (isMigrateWebsite || isRelevantNonMigrate);
      })
      .sort((activity1: IActivity, activity2: IActivity) => {
        if (activity1 && activity1.date && activity2 && activity2.date) {
          if (activity1.date.getTime() > activity2.date.getTime() && (activity1.status !== 'done' && activity1.status !== 'failed') && activity1.status !== activity2.status) {
            return -1;
          }
          else if ((activity1.date.getTime() < activity2.date.getTime() || (activity1.status === 'done' || activity1.status === 'failed')) && activity1.status !== activity2.status ) {
            return 1;
          }
        }
        return 0;
      });

    return activities;
  }
);

export const getNotFinishedOngoingActivities = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity[] => {
    const activities: IActivity[] = Object.values(state.entities)
      .filter((websiteActivitiesState: IWebsiteOngoingActivitiesState): boolean => !!websiteActivitiesState.entities)
      .map((websiteActivitiesState: IWebsiteOngoingActivitiesState): IActivity[] => Object.values(websiteActivitiesState.entities))
      .reduce((activities: IActivity[], websiteActivities: IActivity[]): IActivity[] => {
        return activities.concat(websiteActivities);
      }, [])
      .filter((activity: IActivity) => !activity.finished);

    return activities;
  }
);

export const getCreateWebsiteOngoingActivity = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[0]) {
      return null;
    }

    return Object.values(state.entities[0].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        return activity.action === ActivityAction.CREATE_WEBSITE;
      });
  }
);

export const getDedicatedInstanceOngoingActivity = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[0]) {
      return null;
    }

    return Object.values(state.entities[0].entities)
      .find((activity: IActivity): boolean => {
        return activity.action === ActivityAction.CREATE_DEDICATED_INSTANCE;
      });
  }
);

export const getAddWebsiteOngoingActivity = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    const activity: IActivity = Object.values(state.entities)
      .filter((websiteActivitiesState: IWebsiteOngoingActivitiesState): boolean => !!websiteActivitiesState.entities)
      .map((websiteActivitiesState: IWebsiteOngoingActivitiesState): IActivity[] => Object.values(websiteActivitiesState.entities))
      .reduce((activities: IActivity[], websiteActivities: IActivity[]): IActivity[] => {
        return activities.concat(websiteActivities);
      }, [])
      .find((activity: IActivity): boolean => activity.action === ActivityAction.ADD_WEBSITE && activity.status === ActivityStatus.DONE);

    return activity;
  }
);

export const getRemoveWebsiteOngoingActivity = (websiteId: number) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (state.entities[websiteId]) {
      const activity: IActivity = Object.values(state.entities[websiteId])
        .filter((activityState: IActivity[]): boolean => !!activityState)
        .map((activityState: IActivity[]): IActivity[] => Object.values(activityState))
        .reduce((activities: IActivity[], websiteActivities: IActivity[]): IActivity[] => {
          return activities.concat(websiteActivities);
        }, [])
        .find((activity: IActivity): boolean => activity.action === ActivityAction.REMOVE_WEBSITE);
      return activity;
    }
    else {
      return null;
    }
  }
);

export const getEnableDevEnvironmentOngoingActivity = (liveWebsiteId: number) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    let allOngoingActivities: IActivity[] = Object.values(state.entities).reduce((activities: IActivity[], websiteOngoingActivities: IWebsiteOngoingActivitiesState): IActivity[] => {
      return activities.concat(Object.values(websiteOngoingActivities.entities));
    }, []);

    allOngoingActivities = allOngoingActivities.filter((activity: IActivity): boolean => !activity.finished);

    return allOngoingActivities.find((activity: IActivity): boolean => {
      return activity.action === ActivityAction.CREATE_STAGING_WEBSITE && activity.meta && activity.meta.live_domain_id === liveWebsiteId;
    });
  }
);

export const getDisableDevEnvironmentOngoingActivity = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    let allOngoingActivities: IActivity[] = Object.values(state.entities).reduce((activities: IActivity[], websiteOngoingActivities: IWebsiteOngoingActivitiesState): IActivity[] => {
      return activities.concat(Object.values(websiteOngoingActivities.entities));
    }, []);

    allOngoingActivities = allOngoingActivities.filter((activity: IActivity): boolean => !activity.finished);

    return allOngoingActivities.find((activity: IActivity): boolean => {
      return activity.action === ActivityAction.DELETE_STAGING_WEBSITE;
    });
  }
);

export const getPushToLiveOngoingActivity = (stagingWebsiteId: number) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    let allOngoingActivities: IActivity[] = Object.values(state.entities).reduce((activities: IActivity[], websiteOngoingActivities: IWebsiteOngoingActivitiesState): IActivity[] => {
      return activities.concat(Object.values(websiteOngoingActivities.entities));
    }, []);

    allOngoingActivities = allOngoingActivities.filter((activity: IActivity): boolean => !activity.finished);

    return allOngoingActivities.find((activity: IActivity): boolean => {
      return activity.action === ActivityAction.PUSH_TO_LIVE;
    });
  }
);

export const gePushToStagingOngoingActivity = (stagingWebsiteId: number) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    let allOngoingActivities: IActivity[] = Object.values(state.entities).reduce((activities: IActivity[], websiteOngoingActivities: IWebsiteOngoingActivitiesState): IActivity[] => {
      return activities.concat(Object.values(websiteOngoingActivities.entities));
    }, []);

    allOngoingActivities = allOngoingActivities.filter((activity: IActivity): boolean => !activity.finished);

    return allOngoingActivities.find((activity: IActivity): boolean => {
      return activity.action === ActivityAction.PUSH_TO_STAGING;
    });
  }
);

export const getMigrationOngoingActivity = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    let allOngoingActivities: IActivity[] = Object.values(state.entities).reduce((activities: IActivity[], websiteOngoingActivities: IWebsiteOngoingActivitiesState): IActivity[] => {
      return activities.concat(Object.values(websiteOngoingActivities.entities));
    }, []);

    allOngoingActivities = allOngoingActivities.filter((activity: IActivity): boolean => !activity.finished);

    return allOngoingActivities.find((activity: IActivity): boolean => {
      return activity.action === ActivityAction.MIGRATE_WEBSITE;
    });
  }
);

export const getAiGenerateWebsiteOngoingActivity = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    let allOngoingActivities: IActivity[] = Object.values(state.entities).reduce((activities: IActivity[], websiteOngoingActivities: IWebsiteOngoingActivitiesState): IActivity[] => {
      return activities.concat(Object.values(websiteOngoingActivities.entities));
    }, []);

    allOngoingActivities = allOngoingActivities.filter((activity: IActivity): boolean => !activity.finished);

    return allOngoingActivities.find((activity: IActivity): boolean => {
      return activity.action === ActivityAction.AI_GENERATE_WEBSITE;
    });
  }
);

export const getProductOngoingActivity = (websiteId: number, productSlug: string) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        return !TypeUtils.isEmpty(activity.meta) && activity.meta.product_data ? activity.meta.product_data.slug === productSlug : false;
      });
  }
);

export const getProductUploadOngoingActivity = (websiteId: number) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        return [ActivityAction.INSTALL, ActivityAction.INSTALL_ACTIVATE, ActivityAction.INSTALL_ACTIVATE_UPLOAD].includes(<any>activity.action) && TypeUtils.isEmpty(activity.meta);
      });
  }
);

export const getBackupOngoingActivity = (websiteId: number, backupPluginSlug: string) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        // TODO: STORE: get particular backup/checkpoint activity
        const isBackupPluginActivity: boolean = !TypeUtils.isEmpty(activity.meta) &&  activity.meta.product_data ? activity.meta.product_data.slug === backupPluginSlug : false;
        const isBackupServiceActivity: boolean = [ActivityAction.RUN_JOB, ActivityAction.RESTORE, ActivityAction.BACKUP, ActivityAction.PROCESS_DOWNLOAD].includes(<any>activity.action);

        return isBackupPluginActivity || isBackupServiceActivity;
      });
  }
);

export const getBackupListOngoingActivity = (websiteId: number) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        const isBackupServiceActivity: boolean = [ActivityAction.BACKUP].includes(<any>activity.action);

        return isBackupServiceActivity;
      });
  }
);

export const getImageOptimizerOngoingActivity = (websiteId: number, imageOptimizerPluginSlug: string) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        const isImageOptimizerPluginActivity: boolean = !TypeUtils.isEmpty(activity.meta) && activity.meta.product_data ? activity.meta.product_data.slug === imageOptimizerPluginSlug : false;
        const isImageOptimizerServiceActivity: boolean = [ActivityAction.OPTIMIZE].includes(<any>activity.action);

        return isImageOptimizerPluginActivity || isImageOptimizerServiceActivity;
      });
  }
);

export const getOptimizerOngoingActivity = (websiteId: number, optimizerPluginSlug: string) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        const isOptimizerPluginActivity: boolean = activity.action !== "install_template" &&  activity.action !== "install_hosted_template" && !TypeUtils.isEmpty(activity.meta) && activity.meta.product_data ? activity.meta.product_data.slug === optimizerPluginSlug : false;
        return isOptimizerPluginActivity;
      });
  }
);

export const getSeoOngoingActivity = (websiteId: number, seoPluginSlug: number|string) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        const isSeoPluginActivity: boolean = !TypeUtils.isEmpty(activity.meta) && activity.meta.product_data ? activity.meta.product_data.slug === seoPluginSlug : false;

        return isSeoPluginActivity;
      });
  }
);

export const getBuilderOngoingActivity = (websiteId: number, builderPluginSlug: number|string, hostedWebsite: boolean) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        const isBuilderPluginActivity: boolean = [ActivityAction.IMPORT_HOSTED_TEMPLATE, ActivityAction.IMPORT_TEMPLATE].includes(<any>activity.action);
        return isBuilderPluginActivity;
      });
  }
);

export const getPerformanceOngoingActivity = (websiteId: number) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        const isPerformanceServiceActivity: boolean = [ActivityAction.PERFORMANCE_CHECK].includes(<any>activity.action);

        return isPerformanceServiceActivity;
      });
  }
);

export const getSecurityOngoingActivity = (websiteId: number) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        const isSecurityPluginActivity: boolean = !TypeUtils.isEmpty(activity.meta) && activity.meta.product_data ? activity.meta.product_data.slug === Config.securityPluginSlug : false;
        const isSecurityServiceActivity: boolean = [ActivityAction.SECURITY_SCAN].includes(<any>activity.action);

        return isSecurityPluginActivity || isSecurityServiceActivity;
      });
  }
);

export const getOngoingActivitiesInited = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): boolean =>
    state.inited
);

export const getOngoingActivitiesToggled = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): boolean =>
    state.toggled
);

export const getAiCreateWebsiteOngoingActivity = (websiteId: number) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }

    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => {
        const isAiCreateWebsiteActivity: boolean = [ActivityAction.AI_CREATE_WEBSITE].includes(<any>activity.action);

        return isAiCreateWebsiteActivity;
      });
  }
);

export const getGoogleScoreOngoingActivity = (websiteId: number) => createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    if (!state.entities[websiteId]) {
      return null;
    }
    return Object.values(state.entities[websiteId].entities)
      .filter((activity: IActivity): boolean => !activity.finished)
      .find((activity: IActivity): boolean => activity.action === ActivityAction.PERFORMANCE_GOOGLE_SCORE);
  }
);

export const getRestoreOngoingActivity = createSelector(getOngoingActivitiesState,
  (state: IOngoingActivitiesState): IActivity => {
    const allOngoingActivities: IActivity[] = Object.values(state.entities)
      .reduce((activities: IActivity[], websiteActivities: IWebsiteOngoingActivitiesState): IActivity[] => {
        return activities.concat(Object.values(websiteActivities.entities));
      }, [])
      .filter((activity: IActivity): boolean => !activity.finished);

    return allOngoingActivities.find((activity: IActivity): boolean => activity.action === ActivityAction.RESTORE);
  }
);
